import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, Loader2 } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { useStrings } from '../hooks/useStrings';
import Reveal from '../components/ui/Reveal';

const INPUT_CLASS =
  'w-full px-3 md:px-4 py-2 md:py-3 border border-white/20 rounded-lg bg-infinity-black/40 text-infinity-white placeholder:text-infinity-white/40 focus:ring-2 focus:ring-infinity-yellow focus:border-transparent hover:border-infinity-yellow/50 transition-colors duration-200 text-sm md:text-base';

const Contact: React.FC = () => {
  const { strings } = useStrings();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /**Para comunicarte por WhatsApp */
  const handleWhatsAppContact = () => {
    const phoneNumber = `${strings?.infoEmpresa.telefonos?.atencion}`; // Tu número de WhatsApp (sin signos + ni espacios)
    const message = `Hola soy ${formData.name} 👋

  Estoy interesado en el servicio de *${formData.subject}*.
  *${formData.message}*.
  Me gustaría recibir más información sobre:
  • Detalles específicos del servicio
  • Tiempo de implementación
  • Cotización personalizada

  ¡Gracias!`;

    // URL para WhatsApp Web y móvil
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

    // Abrir en nueva ventana/pestaña
    window.open(whatsappUrl, '_blank');
  };

  const handleWhatsAppClick = () => {
    setIsLoading(true);
    window.setTimeout(() => {
      handleWhatsAppContact();
      setIsLoading(false);
    }, 500);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Configuración de EmailJS - Necesitarás configurar estos valores en tu cuenta de EmailJS
      const result = await emailjs.send(
        'YOUR_SERVICE_ID', // Reemplazar con tu Service ID de EmailJS
        'YOUR_TEMPLATE_ID', // Reemplazar con tu Template ID de EmailJS
        {
          to_email: 'jemd2203@gmail.com',
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          reply_to: formData.email
        },
        'YOUR_PUBLIC_KEY' // Reemplazar con tu Public Key de EmailJS
      );

      console.log('Email sent successfully:', result);
      alert('Mensaje enviado correctamente. Te contactaremos pronto.');

      // Limpiar el formulario
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });

    } catch (error) {
      console.error('Error sending email:', error);
      alert('Hubo un error al enviar el mensaje. Por favor, inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-infinity-black">
      {/* Hero Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-infinity-purple to-infinity-blue text-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1
            className="text-4xl md:text-5xl font-bold mb-4 md:mb-6 opacity-0 animate-fade-in-up"
            style={{ animationDelay: '100ms' }}
          >
            Contáctanos
          </h1>
          <p
            className="text-lg md:text-xl max-w-3xl mx-auto opacity-0 animate-fade-in-up"
            style={{ animationDelay: '250ms' }}
          >
            Estamos aquí para ayudarte con todas tus necesidades en energía renovable
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12 md:py-16 bg-infinity-black">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-infinity-yellow mb-6 md:mb-8">Información de Contacto</h2>

              <div className="space-y-4 md:space-y-6">
                <Reveal delay={0} className="glass rounded-lg p-4 flex items-start space-x-3 md:space-x-4 card-hover">
                  <div className="bg-infinity-yellow/10 rounded-full p-2 shrink-0 transition-transform duration-300 hover:scale-110">
                    <Mail size={20} className="text-infinity-yellow" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-infinity-white text-sm md:text-base">Email</h3>
                    <p className="text-infinity-white/70 text-sm md:text-base">{strings?.infoEmpresa.correos.gerencia}</p>
                    <p className="text-infinity-white/70 text-sm md:text-base">{strings?.infoEmpresa.correos.ventas}</p>
                  </div>
                </Reveal>

                <Reveal delay={80} className="glass rounded-lg p-4 flex items-start space-x-3 md:space-x-4 card-hover">
                  <div className="bg-infinity-yellow/10 rounded-full p-2 shrink-0 transition-transform duration-300 hover:scale-110">
                    <Phone size={20} className="text-infinity-yellow" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-infinity-white text-sm md:text-base">Teléfono</h3>
                    <p className="text-infinity-white/70 text-sm md:text-base">{strings?.infoEmpresa.telefonos.atencion}</p>
                    <p className="text-infinity-white/70 text-sm md:text-base">{strings?.infoEmpresa.telefonos.ventas}</p>
                  </div>
                </Reveal>

                <Reveal delay={160} className="glass rounded-lg p-4 flex items-start space-x-3 md:space-x-4 card-hover">
                  <div className="bg-infinity-yellow/10 rounded-full p-2 shrink-0 transition-transform duration-300 hover:scale-110">
                    <MapPin size={20} className="text-infinity-yellow" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-infinity-white text-sm md:text-base">Dirección</h3>
                    <p className="text-infinity-white/70 text-sm md:text-base">{strings?.infoEmpresa.direccion}<br />Lima, Perú</p>
                  </div>
                </Reveal>

                <Reveal delay={240} className="glass rounded-lg p-4 flex items-start space-x-3 md:space-x-4 card-hover">
                  <div className="bg-infinity-yellow/10 rounded-full p-2 shrink-0 transition-transform duration-300 hover:scale-110">
                    <Clock size={20} className="text-infinity-yellow" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-infinity-white text-sm md:text-base">Horarios</h3>
                    <p className="text-infinity-white/70 text-sm md:text-base">Lunes - Viernes: 10:00 AM - 8:00 PM</p>
                    <p className="text-infinity-white/70 text-sm md:text-base">Sábado: 10:00 AM - 2:00 PM</p>
                    <p className="text-infinity-white/70 text-sm md:text-base">Domingo: Cerrado</p>
                  </div>
                </Reveal>
              </div>

              {/* Map */}
              <Reveal delay={320} className="mt-6 md:mt-8 h-48 md:h-64 rounded-lg overflow-hidden glass p-1">
                <iframe
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  scrolling="no"
                  marginHeight={0}
                  marginWidth={0}
                  className="rounded-lg"
                  src={`https://maps.google.com/maps?width=100%&height=100%&hl=es&q=${encodeURIComponent('-12.0190054, -77.0569198')}&t=&z=16&ie=UTF8&iwloc=&output=embed`}
                  title="Ubicación de Infinitywits"
                />
              </Reveal>
            </div>

            {/* Contact Form */}
            <Reveal delay={100}>
              <h2 className="text-2xl md:text-3xl font-bold text-infinity-yellow mb-6 md:mb-8">Envíanos un Mensaje</h2>

              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6 glass rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-infinity-white/80 mb-2">
                      Nombre Completo *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className={INPUT_CLASS}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-infinity-white/80 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className={INPUT_CLASS}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-infinity-white/80 mb-2">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={INPUT_CLASS}
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-infinity-white/80 mb-2">
                      Asunto *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={handleInputChange}
                      className={INPUT_CLASS}
                    >
                      <option value="" className="bg-infinity-black">Seleccionar asunto</option>
                      <option value="cotizacion" className="bg-infinity-black">Solicitar Cotización</option>
                      <option value="informacion" className="bg-infinity-black">Información General</option>
                      <option value="soporte" className="bg-infinity-black">Soporte Técnico</option>
                      <option value="capacitacion" className="bg-infinity-black">Capacitaciones</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-infinity-white/80 mb-2">
                    Mensaje *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    className={INPUT_CLASS}
                    placeholder="Cuéntanos sobre tu proyecto o consulta..."
                  ></textarea>
                </div>

                <button
                  type="button"
                  onClick={handleWhatsAppClick}
                  disabled={isLoading}
                  className="w-full btn-primary flex items-center justify-center space-x-2 text-base md:text-lg py-3 md:py-4 hover:scale-[1.02] hover:shadow-glow transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      <span>Enviando...</span>
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      <span>Enviar por WhatsApp</span>
                    </>
                  )}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
